/**
 * normalizeEmail — la ÚNICA función válida para normalizar emails en todo el codebase.
 *
 * Cualquier comparación de emails, búsqueda por email, o guardado en base de datos
 * debe pasar por aquí. Si necesitas comparar dos emails, normalízalos ambos y compara
 * los resultados.
 *
 * Rationale: ver docs/decisions/0007-email-normalization.md
 *
 * Comportamiento:
 * - Trim de espacios en blanco.
 * - Lowercase completo.
 * - Opcionalmente: elimina puntos del local part de Gmail (Gmail los ignora).
 * - Opcionalmente: elimina alias +tag (Gmail y otros los ignoran).
 *
 * Por defecto ambas opciones están DESACTIVADAS. Se activan solo cuando quieras
 * agrupar variaciones (ej. detectar si dos emails apuntan a la misma cuenta Gmail).
 */

export interface NormalizeEmailOptions {
  /** Elimina puntos del local part si el dominio es gmail.com / googlemail.com */
  stripGmailDots?: boolean;
  /** Elimina el alias +tag del local part */
  stripPlusAlias?: boolean;
}

export function normalizeEmail(
  email: string,
  options: NormalizeEmailOptions = {}
): string {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed.includes('@')) return trimmed;

  const atIndex = trimmed.lastIndexOf('@');
  let local = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);

  if (options.stripPlusAlias) {
    const plusIndex = local.indexOf('+');
    if (plusIndex !== -1) {
      local = local.slice(0, plusIndex);
    }
  }

  if (options.stripGmailDots && (domain === 'gmail.com' || domain === 'googlemail.com')) {
    local = local.replace(/\./g, '');
  }

  return `${local}@${domain}`;
}

/** Compara dos emails aplicando la misma normalización. */
export function emailsMatch(a: string, b: string, options?: NormalizeEmailOptions): boolean {
  return normalizeEmail(a, options) === normalizeEmail(b, options);
}

/** Validación básica de formato (RFC 5322 simplificado). */
export function isValidEmail(email: string): boolean {
  if (typeof email !== 'string' || email.length > 254) return false;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}
